import React, { useState, useEffect } from "react";

import Head from 'next/head'
import Link from 'next/link'
import DOMPurify from 'isomorphic-dompurify';

import { TbHandClick } from "react-icons/tb";
import { RiCheckboxCircleLine } from "react-icons/ri";
import { FaMapMarkerAlt, FaHardHat, FaWrench, FaCheckSquare, FaArrowCircleLeft } from "react-icons/fa";
import { RiFlashlightFill, RiEditLine, RiSettings5Line } from "react-icons/ri";
import { BiStore } from "react-icons/bi";
import { GiRank1, GiRank2, GiRank3, GiConcreteBag, GiWoodPile } from "react-icons/gi";
import { MdOutlineSubtitles } from "react-icons/md";

import UserManager from "../lib/userManager"
import ToastManager from "../lib/toastManager";
import { JobAPI } from "../lib/conduit"

import JobAttributes from "./JobAttributes"
import ApplyModal from "./ApplyModal"
import styles from './JobView.module.css'
import PromoteBlock from './PromoteBlock'

export default function JobView({jobData, applied}) {
  if (!jobData) return(<></>)

  const [job, setJob] = useState(jobData)
  const [showApply, setShowApply] = useState(false)
  const [showApplyBtn, setShowApplyBtn] = useState(!applied)
  const [user, setUser] = useState({})

  useEffect(() => setUser(UserManager.buildUser()), [])

  if(!job) return(<></>)

  const buttonStyle = () => {
    if(job.secondary_color) {
      return {
        background: job.secondary_color,
        borderColor: job.secondary_color,
        color: getContrast(job.secondary_color)
      }
    }

    return null;
  }

  const headerStyle = () => {
    if(job.primary_color) {
      return {
        color: getContrast(job.primary_color)
      }
    } else {
      return {
        color: "#fff"
      }
    }
  }

  const getContrast = (hexcolor) => {
    if(!hexcolor) return;

    // If a leading # is provided, remove it
    if (hexcolor.slice(0, 1) === '#') {
      hexcolor = hexcolor.slice(1);
    }
  
    // If a three-character hexcode, make six-character
    if (hexcolor.length === 3) {
      hexcolor = hexcolor.split('').map(function (hex) {
        return hex + hex;
      }).join('');
    }
  
    // Convert to RGB value
    var r = parseInt(hexcolor.substr(0,2),16);
    var g = parseInt(hexcolor.substr(2,2),16);
    var b = parseInt(hexcolor.substr(4,2),16);
  
    // Get YIQ ratio
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  
    // Check contrast
    return (yiq >= 128) ? 'black' : 'white';
  
  };

  const setWorkTypeIcon= () => {
    if(!job.work_type) return;

    if(job.work_type.name === "Construction") {
      return <FaHardHat className={styles["highlight-icon"]}/> 
    } else if(job.work_type.name === "Maintenance") {
      return <FaWrench className={styles["highlight-icon"]}/> 
    }
  }

  const setExperienceIcon = () => {
    const exp = job.min_years_of_experience;

    if(!exp) return;

    if(exp < 5) {
      return <GiRank1 className={styles["highlight-icon"]}/>
    } else if(exp > 5 && exp < 15) {
      return <GiRank2 className={styles["highlight-icon"]}/>
    } else {
      return <GiRank3 className={styles["highlight-icon"]}/>
    }
  }

  const setTradeIcon = () => {
    if(job.trade.name === "Electrical") {
      return(<><RiFlashlightFill className={styles["highlight-icon"]}/> {job.trade.name || "N/A"}</>)
    } else if(job.trade.name === "Concrete") {
      return(<><GiConcreteBag className={styles["highlight-icon"]}/> {job.trade.name || "N/A"}</>)
    } else if(job.trade.name === "Carpentry") {
      return(<><GiWoodPile className={styles["highlight-icon"]}/> {job.trade.name || "N/A"}</>)
    } else {
      return(<><RiFlashlightFill className={styles["highlight-icon"]}/> {job.trade.name || "N/A"}</>)
    }
  }

  const buildProjectTypesBox = () => {
    return(
      <JobSubBox title={"Project Types"} color={job.secondary_color}>
        <JobAttributes data={job.project_types}/>
      </JobSubBox>
    )
  }

  const buildSkillsBox = () => {
    return(
      <JobSubBox title={"Skills"} color={job.secondary_color}>
        <JobAttributes data={job.skills}/>
      </JobSubBox>
    )
  }

  const buildCertificationsBox = () => {
    return(
      <JobSubBox title={"Certifications"} color={job.secondary_color}>
        <JobAttributes data={job.certifications}/>
      </JobSubBox>
    )
  }

  const buildPersonalAttributesBox = () => {
    const elements = job.personal_attributes.map(attr => <span key={attr} className="badge badge-primary">{attr}</span>)
    
    if(elements.length === 0) return;

    return(
      <JobSubBox title={"Candidate Requirements"}  color={job.secondary_color}>
        {elements.length > 0 ? elements : "None found"}
      </JobSubBox>
    )
  }

  const buildDescriptionBox = () => {
    return(
      <JobSubBox title={"Description"} color={job.secondary_color}>
        <div  dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(job.description || "<p>No description added</p>")}}></div>
      </JobSubBox>
    )
  }

  const buildCompBox = () => {
    const benefitsElements = job.benefits.map(benefit => {   
      return(
        <span className={styles["benefits-item"] + " col-sm-12"}>
          <FaCheckSquare style={{marginRight: 5}}/>
          {benefit}
        </span>
      )
    })

    return(
      <JobSubBox title={"Compensation"} color={job.secondary_color}>
        <div className={styles["comp-container"]}>
          <p className={styles["comp-subtitle"]}>Wage</p>
          <p className={styles["comp-text"]}>${job.min_wage} - ${job.max_wage} <span className={styles["comp-text-small"]}>/ hour</span></p>
        </div>
        <div className={styles["comp-container"]}>
          <p className={styles["comp-subtitle"]}>{benefitsElements.length > 0 ? "Benefits" : ""}</p>
          <div className={styles["benefits-row"] + " row"}>
            {benefitsElements}
          </div>
        </div>
      </JobSubBox>
    )
  }
  
  const quickApply = async () => {
    const authToken = UserManager.getAuthToken();
    const identifier = job.identifier;

    const res = await JobAPI.quickApply(authToken, identifier);

    if(res.success) {
      setShowApplyBtn(false);
      ToastManager.successfullJobApply();
    } else {
      if(res.errors[0] === "You have already applied to this job") {
        setShowApplyBtn(false);
      }
      ToastManager.errorToast(res.errors.join(", "))
    }
  }

  const handleApply = async () => {
    if(UserManager.signedIn() && UserManager.isWorker()) {
      await quickApply();
    } else {
      setShowApply(true);
    }
  }

  const pullData = async () => {
    const data = await JobAPI.view(UserManager.getAuthToken(), job.identifier)

    setJob(data.job)
  }

  const buildCTA = () => {
    if((user != null && user.employer != null && job.employer_identifier === user.employer.identifier)) {
      return(
        <div className='row'>
          <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
            <Link href={"/jobs/" + job.identifier + "/edit"}>
              <a style={{color: "#fff", ...headerStyle()}} className={"btn btn-primary btn-block"}><RiEditLine style={{marginRight: 5}}/> Edit Job</a>
            </Link>
          </div>
          <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
            <Link href={"/jobs/" + job.identifier + "/manage"}>
              <a className={"btn btn-secondary btn-block"}><RiSettings5Line style={{marginRight: 5, marginBottom: 2}}/> Manage Job</a>
            </Link>
          </div>
          <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
            <PromoteBlock job={job} pullData={pullData}/>
          </div>
          <div className='col-sm-12 col-md-6 mx-auto'></div>
        </div>
      )
    } else if (UserManager.isAdmin()) {
        return(
          <div className='row'>
            <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
              <Link href={"/admin/jobs/" + job.identifier + "/edit"}>
                <a style={{color: "#fff", ...headerStyle()}} className={"btn btn-primary btn-block"}><RiEditLine style={{marginRight: 5}}/> Edit Job</a>
              </Link>
            </div>
            <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
              <Link href={"/admin/jobs/" + job.identifier}>
                <a className={"btn btn-secondary btn-block"}><RiSettings5Line style={{marginRight: 5, marginBottom: 2}}/> Manage Job</a>
              </Link>
            </div>
            <div className='col-sm-12 col-md-2 mb-2 mx-auto'>
              <PromoteBlock job={job} pullData={pullData}/>
            </div>
            <div className='col-sm-12 col-md-6 mx-auto'></div>
          </div>
        )
    } else {
      if(showApplyBtn) {
        return(
          <div className={'row'}>
            <div className={'col-sm-12 col-md-2'}>
              <button className={"btn btn-block btn-primary btn-lg"} style={{...buttonStyle(), fontWeight: "bold", fontSize: "1.1rem"}} onClick={handleApply}>Apply now &nbsp;<TbHandClick /></button>
            </div>
            <div className={'col-sm-12 col-md-10'}></div>
          </div>
        ) 
      } else {
        return(
          <div className={'row'}>
            <div className={'col-sm-12 col-md-2'}>
              <button className={"btn btn-block btn-secondary btn-lg"} style={{...buttonStyle(), fontWeight: "bold", fontSize: "1.1rem"}} onClick={() => {}}>Application Submitted &nbsp;<RiCheckboxCircleLine /></button>
            </div>
            <div className={'col-sm-12 col-md-10 p-0 m-0'}></div>
          </div>
        )
      }
    }
  }

  const structuredData = () => {
    const sData = {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "title": job.name,
      "description": job.description,
      "datePosted": job.created_at,
      "employmentType": "FULL-TIME",
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.employer,
        "sameAs": "http://www.meetladder.com",
        "logo": "http://www.meetladder.com/conduit_horizontal_white.png"
      },
      "jobLocation": {
        "@type": "Place",
          "address": {
          "@type": "PostalAddress",
          "addressLocality": job.city,
          "addressRegion": job.state,
          "addressCountry": "US"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": job.min_wage,
          "maxValue": job.max_wage,
          "unitText": "HOUR"
        }
      }
    }

    return (
      <script type="application/ld+json">
        {JSON.stringify(sData).replace("quot;", "\"")}
      </script>
    );
  }


  const updateShowApply = (show) => setShowApply(show)

  let yearsStr = (job.min_years_of_experience !== null || job.min_years_of_experience !== undefined) ? job.min_years_of_experience : "N/A";
  if(yearsStr != "N/A") yearsStr += job.min_years_of_experience != 1 ? " Years" : " Year"

  return (
    <div className="container">
      <Head>
        {structuredData()}
      </Head>
      <ApplyModal showApply={showApply} onShowApplyUpdate={updateShowApply} job={job} btnColor={job.primary_color} btnTextColor={getContrast(job.primary_color)}/>
      <div className="row justify-content-center">
        <div className="col-xl-11 col-12">
          <Link href={"/e/" + job.employer_slug}>
            <a className={"clean-link"} style={{color: "#fff"}}><FaArrowCircleLeft style={{marginBottom: 1}}/>&nbsp;&nbsp; Back to all jobs at {job.employer}</a>
          </Link>
        </div>
        <div className="col-xl-11 col-lg-12">
          <div className="row">
            <div className="col-12 text-truncate text-wrap">
              <div className={styles["job-header"]} style={headerStyle()}>
                <h1 className={styles["job-name"]} style={headerStyle()}>{job.name}</h1>
              </div>
              <div className={styles["job-subheader"]}>
                <p className={styles["job-employer-and-location"]} style={headerStyle()}>{job.employer} <br className={styles['job-break']}/><FaMapMarkerAlt/> {job.location}</p>
              </div>
            </div>
          </div>
          {buildCTA()}
          <div className={styles["job-highlights"]}>
            <div className="row">
              <div className={styles["highlight-col"]}>
                <p className={styles["highlight-title"]} style={headerStyle()}>
                  Trade
                </p>
                <p className={styles["highlight-text"]} style={headerStyle()}>
                  {setTradeIcon()}
                </p>
              </div>
              <div className={styles["highlight-col"]}>
                <p className={styles["highlight-title"]} style={headerStyle()}>
                  Title
                </p>
                <p className={styles["highlight-text"]}  style={headerStyle()}>
                  <MdOutlineSubtitles className={styles["highlight-icon"]}/>
                  {job.title ? job.title.name : "N/A"}
                </p>
              </div>
              <div className={styles["highlight-col"]}>
                <p className={styles["highlight-title"]} style={headerStyle()}>
                  Work Type
                </p>
                <p className={styles["highlight-text"]} style={headerStyle()}>
                  {setWorkTypeIcon()}
                  {job.work_type ? job.work_type.name : "N/A"} 
                </p>
              </div>
              <div className={styles["highlight-col"]}>
                <p className={styles["highlight-title"]} style={headerStyle()}>
                  Industry
                </p>
                <p className={styles["highlight-text"]} style={headerStyle()}>
                  <BiStore className={styles["highlight-icon"]}/>
                  {job.industry ? job.industry.name : "N/A"}
                </p>
              </div>
              <div className={styles["highlight-col"]}>
                <p className={styles["highlight-title"]} style={headerStyle()}>
                  Minimum Experience
                </p>
                <p className={styles["highlight-text"]} style={headerStyle()}>
                  {setExperienceIcon()}
                  {job.min_years_of_experience} {job.min_years_of_experience != 1 ? "Years" : "Year"}
                </p>
              </div>
            </div>
          </div> 
          <div className="row">
            <div className="col-lg-8">
              {buildDescriptionBox()} 
              {buildPersonalAttributesBox()}
              {buildProjectTypesBox()}
              {buildSkillsBox()}
              {buildCertificationsBox()}
            </div>
            <div className="col-lg-4">
              {buildCompBox()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


function JobSubBox({children, title, color}) {
  return(
    <div className={styles["job-sub-box"]} style={{borderColor: (color ? color : "")}}>
      <h2 className={styles["sub-box-title"]}>{title}</h2>
      {children}
    </div>
  )
}
